/* eslint-disable max-len */
import { ReactElement, useCallback, useEffect, useState } from 'react';
import { Modal, message } from 'antd';

import { Tour } from 'app/types';
import { getLastId } from 'app/helpers';
import { addParamsToUrl } from 'app/utils';
import {
  addCollaborator,
  removeCollaborator,
  getCollaborators,
  CollaboratorInfo,
  recordTourShare,
} from 'app/services/firebase/functions';

import styles from './ShareTourModal.module.scss';

type ShareProps = {
  values: {
    isOpened: boolean;
    close: () => void;
  };
  tour: Tour;
  /** Optional viewer layer (e.g. "unfurnished" for the Empty Space variant)
   *  appended to both the copy-link URL and the embed iframe src.
   *  Ignored when `shareUrl` is provided. */
  layer?: string;
  /** When set, copy-link and embed-code use this URL verbatim — typically
   *  passed in from the parent so the share link mirrors exactly what the
   *  dashboard iframe is showing (start location, layer, etc.). */
  shareUrl?: string;
  /** Sticky preference (set in Manage Layers): when true, the viewer
   *  hides its bottom-left layer pills. Display-only here — the URL
   *  itself is built by the parent and already carries `&hideLayers=1`. */
  hideLayers?: boolean;
  /** Human label for the current default layer (e.g. "Defurnished",
   *  "Original"). Shown in the share-info chips so the user knows which
   *  version of the tour they're about to share. */
  defaultLayerLabel?: string;
};

const LinkIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M7 9a3 3 0 0 0 4.24 0l2.12-2.12a3 3 0 0 0-4.24-4.24L8 4" />
    <path d="M9 7a3 3 0 0 0-4.24 0L2.64 9.12a3 3 0 0 0 4.24 4.24L8 12" />
  </svg>
);

const CodeIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M5 4L2 8L5 12M11 4L14 8L11 12" />
  </svg>
);

const ChevronIcon = ({ open }: { open: boolean }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{
      transform: open ? 'rotate(180deg)' : 'none',
      transition: 'transform 0.15s',
    }}
  >
    <path d="M4 6l4 4 4-4" />
  </svg>
);

const CloseIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M4 4L12 12M12 4L4 12" />
  </svg>
);

function ShareTourModal({
  values,
  tour,
  layer,
  shareUrl,
  hideLayers,
  defaultLayerLabel,
}: ShareProps): ReactElement {
  const { isOpened, close } = values;
  const tourId = getLastId(tour.full_id) ?? '';
  const isOwner = tour.isOwner !== false;
  const title = tour.name;

  // Parent passes a fully-built URL that already encodes start location,
  // layer, and hideLayers. Fallback exists only for callers that don't
  // forward `shareUrl` — and those callers wouldn't have the right state
  // to forward hideLayers either, so we re-derive it inline there too.
  const url =
    shareUrl ??
    (() => {
      const urlParams = [
        { param: 'tour_id', value: tourId },
        { param: 'debug', value: 'false' },
      ];
      if (layer) urlParams.push({ param: 'layer', value: layer });
      if (hideLayers) urlParams.push({ param: 'hideLayers', value: '1' });
      return addParamsToUrl(process.env.REACT_APP_IFRAME_URL, urlParams);
    })();

  const [collaborators, setCollaborators] = useState<CollaboratorInfo[]>([]);
  const [owner, setOwner] = useState<CollaboratorInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [adding, setAdding] = useState(false);
  const [embedOpen, setEmbedOpen] = useState(false);
  const [dimensions, setDimensions] = useState({ width: '800', height: '' });

  // The viewer host (e.g. dev-viewer.the3dapp.com) is for the dashboard's
  // own iframe. For external embeds we always use embed.the3dapp.com, which
  // serves the same tour but is the public-facing hostname.
  const embedUrl = (() => {
    try {
      const u = new URL(url);
      u.hostname = 'embed.the3dapp.com';
      return u.toString();
    } catch {
      return url;
    }
  })();
  // `allow="screen-wake-lock"` lets the embedded viewer hold a wake lock so
  // the screen doesn't dim during a tour. Without it the viewer's wake-lock
  // request rejects (it info-logs and keeps working).
  const iframeCode = `<iframe title="${title}" width="${
    dimensions.width || 'auto'
  }" height="${
    dimensions.height || 'auto'
  }" src="${embedUrl}" allow="screen-wake-lock"></iframe>`;

  const fetchCollabs = useCallback(async () => {
    if (!tourId) return;
    try {
      setLoading(true);
      const res = await getCollaborators(tourId);
      setOwner(res.owner);
      setCollaborators(res.collaborators);
    } catch (err) {
      console.warn('[Share] collaborators fetch failed', err);
    } finally {
      setLoading(false);
    }
  }, [tourId]);

  useEffect(() => {
    if (isOpened) fetchCollabs();
  }, [isOpened, fetchCollabs]);

  const handleAdd = async () => {
    const trimmed = email.trim();
    if (!trimmed) return;
    try {
      setAdding(true);
      const res = await addCollaborator(tourId, trimmed);
      setCollaborators((prev) => [...prev, res.collaborator]);
      setEmail('');
      message.success(`${res.collaborator.email} added`);
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : 'Failed to add collaborator';
      message.error(msg);
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (uid: string) => {
    try {
      await removeCollaborator(tourId, uid);
      setCollaborators((prev) => prev.filter((c) => c.uid !== uid));
      message.success('Collaborator removed');
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : 'Failed to remove collaborator';
      message.error(msg);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      message.success('Link copied to clipboard');
      // Fire-and-forget; recordTourShare is best-effort and logs on failure.
      recordTourShare(tour.full_id);
    } catch {
      message.error('Unable to copy');
    }
  };

  const copyEmbed = async () => {
    try {
      await navigator.clipboard.writeText(iframeCode);
      message.success('Embed code copied to clipboard');
      recordTourShare(tour.full_id);
    } catch {
      message.error('Unable to copy');
    }
  };

  const initialOf = (c: {
    displayName?: string | null;
    email?: string | null;
  }) => (c.displayName?.[0] || c.email?.[0] || '?').toUpperCase();

  return (
    <Modal
      width={560}
      visible={isOpened}
      footer={null}
      centered
      closable={false}
      wrapClassName={styles.shareModalWrap}
      maskStyle={{
        background: 'rgba(0, 0, 0, 0.32)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
      }}
      onCancel={close}
    >
      {/* antd Modal renders into a portal, but React synthetic events still
          bubble through the React tree — so without this, clicks/keys inside
          the modal reach an ancestor's onClick (e.g. the TourCard row that
          opens the tour). Stop propagation at the content root. */}
      <div
        className="relative p-2"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-5">
          <h3 className="text-[20px] font-medium text-[#151A30] m-0 truncate pr-4">
            Share &ldquo;{title}&rdquo;
          </h3>
          <button
            aria-label="Close"
            onClick={close}
            className="btn-toolbar text-black/50 shrink-0"
          >
            <CloseIcon />
          </button>
        </div>

        <div className="mb-6">
          <h4 className="text-[14px] font-semibold text-[#151A30] m-0">
            Public link
          </h4>
        </div>

        <div className="mb-3 flex flex-wrap items-center gap-2 text-[12px] text-black/65">
          <span>Sharing the version</span>
          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-black/[0.06] border border-black/[0.06] text-[11.5px] font-semibold text-black/80">
            {defaultLayerLabel || 'Original'}
          </span>
          {hideLayers && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/[0.06] border border-black/[0.06] text-[11.5px] font-semibold text-black/80">
              <svg
                width="11"
                height="11"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M2 8s2.5-4.5 6-4.5S14 8 14 8s-2.5 4.5-6 4.5S2 8 2 8z" />
                <circle cx="8" cy="8" r="2" />
                <path d="M2 2l12 12" />
              </svg>
              Layer selector hidden
            </span>
          )}
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={copyLink}
            className="inline-flex items-center justify-center gap-2 w-full h-10 px-4 rounded-full bg-black/85 border-0 text-[13px] font-semibold text-white hover:bg-black transition-colors cursor-pointer"
          >
            <LinkIcon />
            Copy link
          </button>
        </div>

        <div className="mt-6 pt-5 border-t border-black/[0.08]">
          <button
            type="button"
            onClick={() => setEmbedOpen((v) => !v)}
            className="w-full flex items-start justify-between gap-3 bg-transparent border-0 p-0 cursor-pointer text-[#151A30]"
            aria-expanded={embedOpen}
          >
            <span className="flex flex-col items-start min-w-0">
              <span className="inline-flex items-center gap-2 text-[14px] font-semibold">
                <CodeIcon />
                Embed on your site
              </span>
              <span className="text-[12px] text-black/55 mt-0.5">
                Add this listing to MLS
              </span>
            </span>
            <ChevronIcon open={embedOpen} />
          </button>

          {embedOpen && (
            <div className="mt-3 p-4 rounded-[14px] bg-black/[0.03] border border-black/[0.06]">
              <div className="flex items-center justify-between mb-3">
                <span className="inline-flex items-center gap-2 text-[11px] font-semibold text-black/55 uppercase tracking-wide">
                  <CodeIcon />
                  Embed
                </span>
                <div className="flex items-center gap-3">
                  <label className="inline-flex items-center gap-1.5 text-[12px] text-black/55">
                    W
                    <input
                      type="text"
                      value={dimensions.width}
                      placeholder="auto"
                      onChange={(e) =>
                        setDimensions((prev) => ({
                          ...prev,
                          width: e.target.value.replace(/[^0-9]/g, ''),
                        }))
                      }
                      className="w-14 bg-white border border-black/15 rounded-[8px] px-2 py-1 text-[12px] text-black/85 outline-none focus:border-black/40 text-center"
                    />
                  </label>
                  <label className="inline-flex items-center gap-1.5 text-[12px] text-black/55">
                    H
                    <input
                      type="text"
                      value={dimensions.height}
                      placeholder="auto"
                      onChange={(e) =>
                        setDimensions((prev) => ({
                          ...prev,
                          height: e.target.value.replace(/[^0-9]/g, ''),
                        }))
                      }
                      className="w-14 bg-white border border-black/15 rounded-[8px] px-2 py-1 text-[12px] text-black/85 outline-none focus:border-black/40 text-center"
                    />
                  </label>
                </div>
              </div>

              <pre className="bg-white border border-black/10 rounded-[10px] p-3 text-[12px] text-black/75 overflow-x-auto whitespace-pre-wrap break-all font-mono m-0 mb-3">
                {iframeCode}
              </pre>

              <button
                type="button"
                onClick={copyEmbed}
                className="btn-primary w-full"
              >
                Copy embed code
              </button>
            </div>
          )}
        </div>

        <div className="mb-5 mt-8">
          <h4 className="text-[14px] font-semibold text-[#151A30] m-0">
            Dashboard Collaborators
          </h4>
        </div>

        {isOwner && (
          <div className="mb-5 relative">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
              placeholder="Invite collaborator by email"
              disabled={adding}
              className="w-full h-12 pl-4 pr-14 bg-white border border-black/15 rounded-[10px] text-[14px] text-black/85 placeholder:text-black/40 outline-none focus:border-black/40 transition-colors"
            />
            {email.trim() && (
              <button
                type="button"
                onClick={handleAdd}
                disabled={adding}
                aria-label="Send invite"
                className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/85 hover:bg-black text-white inline-flex items-center justify-center border-0 cursor-pointer focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M3 8h10M9 4l4 4-4 4" />
                </svg>
              </button>
            )}
          </div>
        )}

        <div className="mb-5">
          {loading ? (
            <div className="flex flex-col gap-3">
              {[0, 1].map((i) => (
                <div key={i} className="flex items-center gap-3 animate-pulse">
                  <div className="w-10 h-10 rounded-full bg-black/10" />
                  <div className="flex-1 flex flex-col gap-1.5">
                    <div className="h-3 w-40 rounded bg-black/10" />
                    <div className="h-2.5 w-24 rounded bg-black/5" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {owner && (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#c93e7a] text-white flex items-center justify-center text-[14px] font-semibold shrink-0">
                    {initialOf(owner)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[14px] font-medium text-[#151A30] m-0 truncate">
                      {owner.displayName || owner.email || 'Owner'}
                    </p>
                    {owner.email && owner.displayName && (
                      <p className="text-[12px] text-black/55 m-0 truncate">
                        {owner.email}
                      </p>
                    )}
                  </div>
                  <span className="text-[14px] text-black/45 shrink-0">
                    Owner
                  </span>
                </div>
              )}

              {collaborators.map((c) => (
                <div key={c.uid} className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-black/85 text-white flex items-center justify-center text-[14px] font-semibold shrink-0">
                    {initialOf(c)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[14px] font-medium text-[#151A30] m-0 truncate">
                      {c.displayName || c.email || c.uid}
                    </p>
                    {c.email && c.displayName && (
                      <p className="text-[12px] text-black/55 m-0 truncate">
                        {c.email}
                      </p>
                    )}
                  </div>
                  {isOwner && (
                    <button
                      type="button"
                      onClick={() => handleRemove(c.uid)}
                      className="btn-toolbar text-black/40 hover:text-red-500 shrink-0"
                      aria-label={`Remove ${c.email}`}
                    >
                      <CloseIcon />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

export default ShareTourModal;
